import Image from "next/image"
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import Link from 'next/link';
import useDeviceType from "hooks/useDeviceType";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { getWelcomeCampaing } from "constants/welcomeCampaing";
import HomePageArrow from "components/Icons/HomePageArrow";
import HomePageArrowRight from "components/Icons/HomePageArrowRight";

interface PromoBannerProps {
  regions: string[];
  background_image: string
  mobile_background_image: string
  background_color: string
  title: string
  title_color: string
  subtitle: string
  subtitle_color: string
  button_text: string
  button_color: string
  button_text_color: string
  button_url: string
  alt_text: string
  title_widths?: string;
  subtitle_widths?: string;
  button_custom_classes?: string;
  mobile_title?: string;
  mobile_subtitle?: string;
  sort_index?: number;
  background_video: string;
  mobile_background_video: string;
  mainBanner?: boolean;
}

export const PromoBanner: React.FC<PromoBannerProps> = (props) => {
  if (!props || !props.regions) return null;
  const {
    button_color,
    button_text_color,
    button_url,
    alt_text,
    title_widths,
    subtitle_widths,
    button_custom_classes,
    mobile_title,
    mobile_subtitle,
    background_image,
    mobile_background_image,
    background_color,
    title,
    title_color,
    subtitle,
    subtitle_color,
    button_text,
    background_video,
    mobile_background_video,
    mainBanner,
  } = props;
  const deviceType = useDeviceType();

  const defaultWidths = {
    title: "max-w-[60%] smx:max-w-[60%] xxsmx:max-w-[60%] xsmx:max-w-[60%]",
    subtitle: "max-w-[60%] smx:max-w-[60%] xxsmx:max-w-[60%] xsmx:max-w-[60%]",
  };

  const defaultButtonClasses = 'bottom-[35px]';
  const titleWidthClasses = title_widths || defaultWidths.title;
  const subtitleWidthClasses = subtitle_widths || defaultWidths.subtitle;
  const buttonClasses = button_custom_classes || defaultButtonClasses;
  const titleText = deviceType.isMobile && mobile_title ? mobile_title : title;
  const subtitleText = deviceType.isMobile && mobile_subtitle ? mobile_subtitle : subtitle;

  // Get the appropriate video URL based on device type
  // const videoUrl = deviceType.isMobile ? mobile_background_video : background_video;
  const imageUrl = deviceType.isMobile ? mobile_background_image : background_image;

  const videoUrl = useMemo(() => {
    console.log(deviceType.isMobile);
    if (!deviceType.isMobile) {
      return background_video;
    }
    return mobile_background_video;
  }, [deviceType.isMobile, mobile_background_video, background_video]);

  return (
    <Link href={button_url} className="block w-full">
      {videoUrl ? (
        <>
          {titleText && (
            <div className="flex items-center justify-center md:hidden gap-2 w-full mb-[24px]">
              <h2
                className={`text-[24px] mb-1 font-bold `}
                style={{ color: title_color }}
                dangerouslySetInnerHTML={{ __html: titleText }}
              />
              <HomePageArrowRight />
            </div>
          )}
          <div className="w-full h-[400px] rounded-lg overflow-hidden">
            <video
              key={videoUrl}
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              poster={imageUrl}
              aria-label={alt_text}
            >
              <source src={videoUrl} type="video/mp4" />
              {/* Fallback for browsers that don't support video */}
              Your browser does not support the video tag.
            </video>
          </div>
        </>
      ) : (
        <div
          className={`w-full ${mainBanner ? 'md:h-[400px] h-[400px]' : 'h-[200px] md:h-[400px]'} overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat relative cursor-pointer`}
          style={{
            backgroundImage: `url(${imageUrl})`,
            backgroundColor: background_color,
            backgroundSize: 'cover',
            backgroundPosition: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }}
          role="img"
          aria-label={alt_text}
        >

          <div className="absolute inset-0 flex flex-col justify-between md:p-8 p-[24px]">
            <div className="flex flex-col">
              <h2
                className={`text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 ${titleWidthClasses}`}
                style={{ color: title_color }}
                dangerouslySetInnerHTML={{ __html: titleText }}
              />
              <p
                className={`text-lg md:text-sm lg:text-base xl:text-lg mb-6 ${subtitleWidthClasses}`}
                style={{ color: subtitle_color }}
                dangerouslySetInnerHTML={{ __html: subtitleText }}
              />
            </div>

            <div className="absolute bottom-0 left-0 p-8">
              <div className={`md:w-[44px] md:h-[44px] w-[32px] h-[32px] rounded-full flex items-center justify-center`} style={{ backgroundColor: button_color }}>
                <div className="md:w-[22px] md:h-[16px] w-[16px] h-[12px]">
                  <HomePageArrow fill={button_text_color} width={deviceType.isMobile ? 16 : 22} height={deviceType.isMobile ? 12 : 16} />
                </div>
              </div>
            </div>

            {/* <div className={`absolute md:left-8 left-2 ${buttonClasses}`}>
            <button
              className="px-6 py-3 md:px-4 md:py-2 lg:px-6 lg:py-3 rounded-md text-lg md:text-base lg:text-lg font-semibold w-max"
              style={{ backgroundColor: button_color, color: button_text_color }}
            >
              {button_text}
            </button>
          </div> */}
          </div>
        </div>
      )}
    </Link>
  )
}

export interface PromoBannersObject {
  firstOrderBanner: PromoBannerProps;
  nonFirstOrderBanner: PromoBannerProps;
  mixMatchBanner: PromoBannerProps;
  promotionBanners: PromoBannerProps[];
  categoryBanner: PromoBannerProps[];
  promoMainBanner: PromoBannerProps;
  yourGlassesBanner: PromoBannerProps;
}

interface HomepageCampaignBannersProps {
  promoBanners: PromoBannersObject;
}

export default function HomepageCampaignBanners({ promoBanners }: HomepageCampaignBannersProps) {
  //sort the categoryBanner by order
  const sortedCategoryBanner = promoBanners.categoryBanner.sort((a: PromoBannerProps, b: PromoBannerProps) => (a.sort_index || 0) - (b.sort_index || 0));
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1224px]  px-4">
        <PromoBanner {...promoBanners.promoMainBanner} mainBanner={true} />
      </div>
      <div className="mx-auto 2xl:mt-6 2xl:mb-2 max-w-[1224px] px-4 mt-3 lg:px-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {
            sortedCategoryBanner && sortedCategoryBanner.length > 0 && sortedCategoryBanner.map((banner: PromoBannerProps) => (
              <PromoBanner {...banner} mainBanner={false} />
            ))
          }
        </div>
      </div>
    </section>
  )
}